import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { School } from '../types/schools/School';
import { preloadSchoolLogos } from '../utils/imagePreloader';
import { useUser } from './UserContext';

interface SchoolsContextType {
  schools: School[];
  loading: boolean;
  error: string | null;
  refreshSchools: () => void;
  loadFeedIconsForSchools: (schoolNames: string[]) => Promise<void>;
  feedIconsLoading: boolean;
}

const SchoolsContext = createContext<SchoolsContextType | undefined>(undefined);

export const useSchools = () => {
  const context = useContext(SchoolsContext);
  if (context === undefined) {
    throw new Error('useSchools must be used within a SchoolsProvider');
  }
  return context;
};

interface SchoolsProviderProps {
  children: ReactNode;
}

export const SchoolsProvider: React.FC<SchoolsProviderProps> = ({ children }) => {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedIconsLoading, setFeedIconsLoading] = useState(false);
  const [feedIconsLoaded, setFeedIconsLoaded] = useState<Set<string>>(new Set());
  const { user } = useUser();

  const fetchSchools = async () => {
    // Don't fetch if user is not authenticated
    if (!user) {
      setLoading(false);
      setSchools([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const db = getDatabase();
      
      // Fetch schools, feed icons, and athletics in parallel
      const [schoolsSnapshot, feedIconSnapshot, athleticsSnapshot] = await Promise.all([
        get(ref(db, 'schools')),
        get(ref(db, 'schoolFeedIcon')),
        get(ref(db, 'athletics'))
      ]);

      if (schoolsSnapshot.exists()) {
        const schoolsData = schoolsSnapshot.val();
        const feedIconData = feedIconSnapshot.exists() ? feedIconSnapshot.val() : {};
        const athleticsData = athleticsSnapshot.exists() ? athleticsSnapshot.val() : {};

        const schoolsArray: School[] = Object.entries(schoolsData)
          .filter(([id, data]: [string, any]) => {
            // Filter out schools that have been archived (have a timestamp in SchoolLastDeleted)
            return !data.SchoolLastDeleted;
          })
          .map(([id, data]: [string, any]) => {
            // Use the schoolID from the school data if available, otherwise fallback to the id from the schools table
            const schoolID = data.SchoolID || id;
            
            return {
              id,
              name: data.SchoolName || '',
              city: data.SchoolCity || '',
              province: data.SchoolProvinceState || '',
              type: data.SchoolType || data.type || 'University',
              enrollment: data.SchoolFTUGCount || 0,
              acceptanceRate: data.acceptanceRate || 0,
              tuition: data.tuition || 0,
              roomAndBoardLow: data.SchoolRoomAndBoardLow || 0,
              roomAndBoardHigh: data.SchoolRoomAndBoardHigh || 0,
              description: data.SchoolDescription || '',
              resultsImage: data.SchoolResultsImageURL || '',
              programs: data.programs || [],
              scholarships: data.scholarships || [],
              events: data.events || [],
              website: data.SchoolWebsite || '',
              image: data.SchoolProfileImageURL || '',
              socialMedia: [], // You can map social media fields here if needed
              athletics: [], // Will be populated below
              admissionRequirements: Array.isArray(data.SchoolBasicRequirements) 
                ? data.SchoolBasicRequirements 
                : (data.SchoolBasicRequirements ? [data.SchoolBasicRequirements] : []),
              feedIcon: feedIconData[schoolID] || '', // Load feed icon immediately
              athleticsNickname: data.AthleticsNickname || '',
              athleticsWebsite: data.AthleticsWebsite || '',
              SchoolWikiURL: data.SchoolWikiURL || '',
              SchoolYoutubeURL: data.SchoolYoutubeURL || '',
              SchoolLastDeleted: data.SchoolLastDeleted || undefined,
              SchoolPhone: data.SchoolPhone || '',
              SchoolTwitterURL: data.SchoolTwitterURL || '',
              SchoolInstagramURL: data.SchoolInstagramURL || '',
              SchoolResultsImageURL: data.SchoolResultsImageURL || '',
              SchoolLocationLatitude: data.SchoolLocationLatitude || '',
              SchoolLocationLongitude: data.SchoolLocationLongitude || '',
              primaryLanguage: data.primaryLanguage || '',
              admissionsMinimum: data.SchoolAdmissionsMin || '',
              ...data
            };
          });

        // Populate athletics data for each school
        const schoolsWithAthletics = schoolsArray.map(school => {
          const schoolAthletics = Object.values(athleticsData).filter((a: any) => 
            a.AthleticsSchoolName === school.name
          ).map((a: any) => ({
            sport: a.AthleticsSport,
            gender: a.AthleticsGender === 'M' ? 'Male'
                  : a.AthleticsGender === 'W' ? 'Female'
                  : a.AthleticsGender // fallback for 'Both' or other values
          }));
          return { ...school, athletics: schoolAthletics };
        });
        
        // Mark all feed icons as loaded since we fetched them all
        const allSchoolNames = schoolsWithAthletics.map(school => school.name);
        setFeedIconsLoaded(new Set(allSchoolNames));
        setSchools(schoolsWithAthletics);
        
        console.log('✅ SchoolsContext: Loaded', schoolsWithAthletics.length, 'schools with feed icons and athletics data');
        console.log('📊 Sample Canadian school data:', schoolsWithAthletics[0]);
        
        // Debug: Check if feed icons are loaded
        const schoolsWithIcons = schoolsWithAthletics.filter(school => school.feedIcon);
        console.log('🎨 SchoolsContext: Schools with feed icons:', schoolsWithIcons.length, 'out of', schoolsWithAthletics.length);
        if (schoolsWithIcons.length > 0) {
          console.log('🎨 Sample school with icon:', schoolsWithIcons[0].name, 'icon:', schoolsWithIcons[0].feedIcon);
        }
        
        // Preload school logos in the background for better performance
        preloadSchoolLogos(schoolsWithAthletics).catch(error => {
          console.warn('⚠️ Failed to preload some school logos:', error);
        });
      } else {
        setSchools([]);
      }
    } catch (err) {
      console.error('Error fetching schools:', err);
      setError('Failed to fetch schools data');
    } finally {
      setLoading(false);
    }
  };

  const loadFeedIconsForSchools = async (schoolNames: string[]) => {
    // Since we now load all feed icons upfront, this function is mainly for edge cases
    // where a school might not have been loaded initially
    try {
      setFeedIconsLoading(true);
      console.log('🔄 SchoolsContext: Checking for missing feed icons for schools:', schoolNames);
      
      const db = getDatabase();
      const schoolFeedIconRef = ref(db, 'schoolFeedIcon');
      const feedIconSnapshot = await get(schoolFeedIconRef);
      const feedIconData = feedIconSnapshot.exists() ? feedIconSnapshot.val() : {};

      // Only update schools that don't already have feed icons
      setSchools(prevSchools => 
        prevSchools.map(school => {
          if (schoolNames.includes(school.name) && !school.feedIcon && !feedIconsLoaded.has(school.name)) {
            const schoolID = school.id;
            const feedIcon = feedIconData[schoolID] || '';
            setFeedIconsLoaded(prev => new Set(Array.from(prev).concat([school.name])));
            return { ...school, feedIcon };
          }
          return school;
        })
      );
      
      console.log('✅ SchoolsContext: Updated feed icons for missing schools');
    } catch (error) {
      console.error('❌ SchoolsContext: Error loading feed icons:', error);
    } finally {
      setFeedIconsLoading(false);
    }
  };

  const refreshSchools = () => {
    fetchSchools();
  };

  useEffect(() => {
    fetchSchools();
  }, [user]);

  const value: SchoolsContextType = {
    schools,
    loading,
    error,
    refreshSchools,
    loadFeedIconsForSchools,
    feedIconsLoading,
  };

  return (
    <SchoolsContext.Provider value={value}>
      {children}
    </SchoolsContext.Provider>
  );
}; 